import { useMemo } from "react";
import dynamic from "next/dynamic";
import Link from "next/link";
import { usePathname, useRouter, useSearchParams } from "next/navigation";
import { parse, stringify } from "qs";

import { Action, Category } from "@/constants/events";
import { StoreMetadata } from "@/custom-types/StoreLocator";
import { useEventTracker } from "@/hooks/useEventTracker";
import { scrollToTop } from "@/utils/scrollPosition";

import Tooltip from "@/components/Tooltip";

const Dropdown = dynamic(() => import("@/components/botanic/Dropdown"), {
  ssr: true,
});

const SortDropdown = ({
  sortConfigurations,
  eventCategory,
  onToggleDropdown,
  sortsAsLinks,
}: {
  sortConfigurations: StoreMetadata["sortConfigurations"];
  eventCategory: Category;
  onToggleDropdown?: () => void;
  sortsAsLinks?: boolean;
}) => {
  const { push } = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const { publishEvent } = useEventTracker();

  const validAvailableSorts = useMemo(() => {
    if (!Array.isArray(sortConfigurations)) {
      return [];
    }

    return sortConfigurations.filter((s) => s?.name && s?.label);
  }, [sortConfigurations]);

  if (validAvailableSorts.length === 0) {
    return <></>;
  }

  const sortByName = new Map(validAvailableSorts.map((s) => [s.name, s]));
  const defaultSort = validAvailableSorts[0];

  const sortValue = searchParams?.get("sort") || defaultSort.name;
  const activeSort = sortByName.get(sortValue) ?? defaultSort;

  const query = parse(searchParams?.toString() || "");
  const { sort: _oldSort, page: _oldPage, ...rest } = query;

  const handleClick = (name: string) => {
    if (!sortsAsLinks) {
      const sortSearchParams = new URLSearchParams(searchParams?.toString());
      sortSearchParams.set("sort", name);
      push(`${pathname}?${sortSearchParams.toString()}`);
    }

    scrollToTop("smooth");

    const clickedSort = sortByName.get(name);
    publishEvent({
      action: Action.click,
      category: eventCategory,
      label: (clickedSort?.label ?? name).toLowerCase(),
    });
  };

  return (
    <div className="flex">
      <Dropdown
        data-testid="sort-dropdown"
        label="Sort by"
        variant="small"
        preselectedOption={String(sortValue)}
        onToggleDropdown={onToggleDropdown}
        options={validAvailableSorts.map((s) => s.name)}
        onItemRender={(optionName) => {
          const orderedParams = {
            sort: optionName,
            ...rest,
          };

          const isHighestRatedWithNoFilters =
            optionName === "highest-rated" && !query.filter && !query.sort;

          const optionSort = sortByName.get(optionName);

          return {
            as: Link,
            children: optionSort?.label ?? optionName,
            "data-testid": `listing-sort-${optionName}`,
            href: `${pathname}?${stringify(orderedParams, { encode: false })}`,
            onClick: () => handleClick(optionName),
            rel: !isHighestRatedWithNoFilters ? "nofollow" : undefined,
          };
        }}
      />

      {activeSort.description ? (
        <Tooltip
          placement="top-right"
          tooltipId="show-finder-sort-tooltip"
          height={16}
          width={16}
          containerClass="!mt-0 ml-sm"
          dialogClass="translate-y-7 w-80 max-w-[28rem]"
          onTooltipClick={() => {
            publishEvent({
              action: Action.click,
              category: Category.finder,
              label: "Sort Dropdown Tooltip",
            });
          }}
        >
          {activeSort.description}
        </Tooltip>
      ) : null}
    </div>
  );
};

export default SortDropdown;
